import { useState, useRef, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MoreVertical, User } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { PlatformIcon } from '../common/PlatformIcon';
import { SocialPost } from './ProjectDetail';

interface KanbanBoardProps {
  posts: SocialPost[];
  onPostStatusChange?: (postId: string, newStatus: SocialPost['status']) => void;
}

const COLUMNS: { id: SocialPost['status']; title: string; color: string }[] = [
  { id: 'draft', title: 'Draft', color: 'bg-cmp-surface' },
  { id: 'in-review', title: 'In Review', color: 'bg-cmp-surface' },
  { id: 'approved', title: 'Approved', color: 'bg-cmp-surface' },
  { id: 'scheduled', title: 'Scheduled', color: 'bg-cmp-surface' },
  { id: 'published', title: 'Published', color: 'bg-cmp-surface' },
];

interface PostCardProps {
  post: SocialPost;
  onStatusChange: (postId: string, newStatus: SocialPost['status']) => void;
}

function PostCard({ post, onStatusChange }: PostCardProps) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'POST',
    item: { id: post.id, status: post.status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dragRef.current) {
      drag(dragRef.current);
    }
  }, [drag]);

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
      case 'x':
        return 'bg-black text-white';
      case 'linkedin':
        return 'bg-blue-600 text-onfill';
      case 'instagram':
        return 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white';
      case 'facebook':
        return 'bg-blue-600 text-onfill';
      case 'youtube':
        return 'bg-red-600 text-onfill';
      case 'threads':
        return 'bg-black text-white';
      case 'tiktok':
        return 'bg-black text-white';
      case 'bluesky':
        return 'bg-sky-500 text-onfill';
      case 'snapchat':
        return 'bg-yellow-400 text-[#17140f]';
      case 'pinterest':
        return 'bg-red-600 text-onfill';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  return (
    <div
      ref={dragRef}
      className={`bg-cmp-raise rounded-cmp2 border border-cmp-line shadow-cmp-e1 cursor-move hover:shadow-cmp-e2 transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
      style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}
    >
      {/* Topic Thumbnail */}
      {post.topic?.mediaUrl && (
        <div className="w-full aspect-video bg-cmp-surface overflow-hidden">
          {post.topic.mediaType === 'VIDEO' ? (
            <video
              src={post.topic.mediaUrl}
              className="w-full h-full object-cover"
              controls={false}
              muted
            />
          ) : (
            <img
              src={post.topic.mediaUrl}
              alt={post.topic.title || 'Topic thumbnail'}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      <div
        className="p-3 space-y-2"
        style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
      >
        <div
          className="flex items-start justify-between gap-2"
          style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
        >
          <h4
            className="text-gray-900 text-sm font-semibold line-clamp-2 flex-1"
            style={{
              minWidth: 0,
              maxWidth: '100%',
              wordBreak: 'break-word',
              overflow: 'hidden',
              overflowWrap: 'break-word',
            }}
          >
            {post.title}
          </h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuItem>Duplicate</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <p
          className="text-gray-600 text-xs line-clamp-2"
          style={{
            width: '100%',
            maxWidth: '100%',
            wordBreak: 'break-word',
            overflow: 'hidden',
            overflowWrap: 'break-word',
          }}
        >
          {post.content}
        </p>

        {/* Post Type */}
        {post.postType && (
          <Badge
            className="bg-cmp-surface text-cmp-ink-2 border border-cmp-line text-xs"
            variant="secondary"
          >
            {post.postType}
          </Badge>
        )}

        {/* Social Accounts */}
        <div className="flex flex-wrap gap-1" style={{ width: '100%', maxWidth: '100%' }}>
          {post.targets && post.targets.length > 0 ? (
            <>
              {post.targets.slice(0, 2).map((target: any) => (
                <Badge
                  key={target.id}
                  className={`${getPlatformColor(target.platform)} flex items-center gap-1 text-xs px-1.5 py-0.5`}
                  variant="secondary"
                  style={{ maxWidth: '100%', flexShrink: 1 }}
                >
                  <span className="flex-shrink-0">
                    <PlatformIcon platform={target.platform} size={10} />
                  </span>
                  <span className="truncate" style={{ maxWidth: '60px' }}>
                    {target.socialProfile?.profileName || target.platform}
                  </span>
                </Badge>
              ))}
              {post.targets.length > 2 && (
                <Badge
                  className="bg-cmp-surface text-cmp-ink-2 text-xs px-1.5 py-0.5 flex-shrink-0"
                  variant="secondary"
                >
                  +{post.targets.length - 2}
                </Badge>
              )}
            </>
          ) : (
            <>
              {post.platforms.slice(0, 2).map((platform) => (
                <Badge
                  key={platform}
                  className={`${getPlatformColor(platform)} flex items-center gap-1 text-xs px-1.5 py-0.5`}
                  variant="secondary"
                  style={{ maxWidth: '100%', flexShrink: 1 }}
                >
                  <span className="flex-shrink-0">
                    <PlatformIcon platform={platform} size={10} />
                  </span>
                  <span className="truncate" style={{ maxWidth: '60px' }}>
                    {platform}
                  </span>
                </Badge>
              ))}
              {post.platforms.length > 2 && (
                <Badge
                  className="bg-cmp-surface text-cmp-ink-2 text-xs px-1.5 py-0.5 flex-shrink-0"
                  variant="secondary"
                >
                  +{post.platforms.length - 2}
                </Badge>
              )}
            </>
          )}
        </div>

        <div
          className="flex items-center justify-between pt-2 border-t border-gray-100"
          style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
        >
          {post.assignedTo ? (
            <div className="flex items-center gap-1.5 flex-1 min-w-0" style={{ minWidth: 0 }}>
              <div className="w-5 h-5 rounded-full bg-cmp-bronze-soft flex items-center justify-center text-cmp-bronze-2 text-xs flex-shrink-0">
                {post.assignedTo.charAt(0).toUpperCase()}
              </div>
              <span className="text-gray-600 text-xs truncate" style={{ minWidth: 0 }}>
                {post.assignedTo.split('@')[0]}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-gray-400 flex-1 min-w-0">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="text-xs truncate">Unassigned</span>
            </div>
          )}
          {post.scheduledDate && (
            <span className="text-gray-500 text-xs flex-shrink-0 ml-2">
              {new Date(post.scheduledDate).toLocaleDateString('default', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface ColumnProps {
  column: { id: SocialPost['status']; title: string; color: string };
  posts: SocialPost[];
  onStatusChange: (postId: string, newStatus: SocialPost['status']) => void;
}

function Column({ column, posts, onStatusChange }: ColumnProps) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'POST',
    drop: (item: { id: string; status: SocialPost['status'] }) => {
      if (item.status !== column.id) {
        onStatusChange(item.id, column.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dropRef.current) {
      drop(dropRef.current);
    }
  }, [drop]);

  return (
    <div
      className="flex flex-col h-full"
      style={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden' }}
    >
      <div
        className={`${column.color} rounded-lg p-3 mb-3 flex-shrink-0`}
        style={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}
      >
        <h3
          className="text-gray-900 truncate"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {column.title}
        </h3>
        <p className="text-gray-600">{posts.length}</p>
      </div>

      <div
        ref={dropRef}
        className={`flex-1 space-y-3 min-h-[500px] p-2 rounded-lg transition-colors overflow-y-auto`}
        style={{
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          ...(isOver
            ? { backgroundColor: '#dbeafe', border: '2px dashed #60a5fa' }
            : { backgroundColor: '#e5e7eb' }),
        }}
      >
        {posts.map((post) => (
          <PostCard key={post.id} post={post} onStatusChange={onStatusChange} />
        ))}
        {posts.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-400">Drop posts here</div>
        )}
      </div>
    </div>
  );
}

export function KanbanBoard({ posts, onPostStatusChange }: KanbanBoardProps) {
  const [localPosts, setLocalPosts] = useState(posts);

  // Sync local state with props when posts change
  useEffect(() => {
    setLocalPosts(posts);
  }, [posts]);

  const handleStatusChange = (postId: string, newStatus: SocialPost['status']) => {
    setLocalPosts((prevPosts) =>
      prevPosts.map((post) => (post.id === postId ? { ...post, status: newStatus } : post))
    );
    onPostStatusChange?.(postId, newStatus);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="overflow-x-auto pb-4 w-full" style={{ maxWidth: '100%', width: '100%' }}>
        <div
          className="grid grid-cols-5 gap-4"
          style={{ minWidth: '1200px', width: '100%', gridAutoColumns: '1fr' }}
        >
          {COLUMNS.map((column) => {
            const columnPosts = localPosts.filter((post) => post.status === column.id);
            return (
              <div
                key={column.id}
                style={{ minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden' }}
              >
                <Column column={column} posts={columnPosts} onStatusChange={handleStatusChange} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 p-4 bg-cmp-surface border border-cmp-line rounded-cmp2 flex-shrink-0">
        <p className="text-blue-800">
          💡 <strong>Tip:</strong> Drag and drop posts between columns to update their status
        </p>
      </div>
    </DndProvider>
  );
}
